import { stationsApi } from '@/api/operations'

export interface StationGeoTag {
  lat: number | null
  lng: number | null
  floorId: number | null
}

/**
 * The pin each station holds on the server, by station id.
 *
 * The station PUT is a full replace, but the /api/station list the grid is
 * built from does NOT return the geo-tag (grsCoordinatedLatitude/Longitude).
 * A write built from a grid row alone sends null and wipes the pin
 * (SCRUM-1144). The map-overlay endpoint does return it, so every update path
 * reads it from there and round-trips it.
 *
 * The floor is part of the pin: both maps draw a station only on its own
 * floor, so a pin with no floor is coordinates nobody sees. The row's floor
 * goes stale when Geotag Device pins a station while the grid is in edit
 * mode, so it is read from here too.
 *
 * A failed lookup gives an empty map rather than throwing: it must not block
 * the save, and the row's own value is used instead.
 */
/**
 * The same lookup, failing loudly. The autosave uses this: on a failed or
 * hung read it must not write, because every update would send the row's
 * null pins and wipe the stations' geotags (SCRUM-1032 round 7 review).
 */
export const readStationGeoTags = async (
  interfaceId: string | number | null,
): Promise<Map<number, StationGeoTag>> => {
  const geoById = new Map<number, StationGeoTag>()
  const geoRes: any = await stationsApi.getAll(Number(interfaceId))
  const geoList = Array.isArray(geoRes) ? geoRes : (geoRes?.data ?? [])
  geoList.forEach((station: any) => {
    geoById.set(Number(station.id), {
      lat: station.grsCoordinatedLatitude ?? null,
      lng: station.grsCoordinatedLongitude ?? null,
      floorId: station.floorId ?? null,
    })
  })
  return geoById
}

export const fetchStationGeoTags = async (
  interfaceId: string | number | null,
): Promise<Map<number, StationGeoTag>> => {
  try {
    return await readStationGeoTags(interfaceId)
  } catch (error) {
    // See above: the save goes ahead with the row value.
    console.error('Failed to read station geo-tags:', error)
    return new Map()
  }
}

/**
 * The pin to send for a station, coordinates and floor together. A pin staged
 * in the grid wins (the GPS picker stages one, with its floor); otherwise the
 * server's current pin, so an edit to anything else cannot silently wipe it.
 * A station the map endpoint does not list keeps the row's floor.
 */
export const resolveGeoTag = (
  station: any,
  geoById?: Map<number, StationGeoTag>,
) => {
  const geo = geoById?.get(Number(station.id))
  const isStaged =
    typeof station.grsCoordinatedLatitude === 'number' &&
    typeof station.grsCoordinatedLongitude === 'number'
  if (isStaged) {
    return {
      grsCoordinatedLatitude: station.grsCoordinatedLatitude,
      grsCoordinatedLongitude: station.grsCoordinatedLongitude,
      floorId: station.floorId ?? geo?.floorId ?? null,
    }
  }
  return {
    grsCoordinatedLatitude: geo?.lat ?? null,
    grsCoordinatedLongitude: geo?.lng ?? null,
    floorId: geo ? geo.floorId : (station.floorId ?? null),
  }
}
